import React, { useState, useEffect, useRef } from "react";
import { searchProjectQualStepsByName } from "../../services/salesforceApi";

/**
 * SearchableLookupField Component
 *
 * A generic searchable dropdown field for Salesforce lookups
 *
 * @param {string} label - Field label
 * @param {string} value - Current value (Name or display value)
 * @param {function} onChange - Callback when value changes: (displayValue, id) => void
 * @param {function} searchFunction - Async function to search: (query) => Promise<Array<{Id, Name}>>
 * @param {string} placeholder - Input placeholder
 * @param {boolean} required - Is field required
 * @param {boolean} disabled - Is field disabled
 * @param {number} minChars - Minimum characters to trigger search (default: 3)
 */
const SearchableLookupField = ({
  label,
  value = "",
  displayValue,
  onChange,
  searchFunction,
  searchParams = {},
  placeholder = "Type to search...",
  required = false,
  disabled = false,
  showError = false,
  minChars = 3,
}) => {
  const [searchQuery, setSearchQuery] = useState(value || "");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Update searchQuery when value prop changes
  useEffect(() => {
    const display = displayValue || value || "";
    setSearchQuery(display);
  }, [value, displayValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle search with debouncing
  const handleSearch = async (query) => {
    if (query.length < minChars) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    setShowDropdown(true);

    try {
      const projectId = searchParams?.initialValues?.projectId;
      const projectObjectiveId =
        searchParams?.initialValues?.projectObjectiveId;
      let results = [];
      if (projectId) {
        if (projectObjectiveId) {
          console.log(
            "🔍 Searching with query:",
            query,
            "and projectId:",
            projectId,
            "and projectObjectiveId:",
            projectObjectiveId,
          );
          results = await searchFunction(query, projectId, projectObjectiveId);
        } else {
          console.log(
            "🔍 Searching with query:",
            query,
            "and projectId:",
            projectId,
          );
          results = await searchFunction(query, projectId);
        }
      } else {
        console.log("🔍 Searching with query:", query);
        results = await searchFunction(query);
      }

      console.log("✅ Search results:", results);
      setSearchResults(results || []);
    } catch (error) {
      console.error("❌ Error searching:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);

    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(newValue);
    }, 300);

    // Update parent with display value
    onChange(newValue, null);
  };

  const handleSelectItem = (item) => {
    setSearchQuery(item.Name);
    setShowDropdown(false);
    setSearchResults([]);

    // Pass both display value and ID to parent
    onChange(item.Name, item.Id);
  };

  // Determine border color
  const getBorderColor = () => {
    if (showError && required && !value && !searchQuery)
      return "border-red-500 bg-red-50";
    return "border-gray-300";
  };
  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 text-sm border ${getBorderColor()} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
            disabled
              ? "bg-gray-50 cursor-not-allowed text-gray-500"
              : "hover:border-gray-400"
          } pr-10`}
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <svg
            className="w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Search Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {isSearching ? (
            <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
          ) : searchResults.length > 0 ? (
            <ul>
              {searchResults.map((item) => (
                <li
                  key={item.Id}
                  onClick={() => handleSelectItem(item)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0"
                >
                  <div className="text-sm font-medium text-gray-900">
                    {item.Name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {searchFunction === searchProjectQualStepsByName
                      ? item.QualStepName
                      : item.PageType
                        ? `${item.PageType} | Project: ${item.ProjectName || "N/A"}`
                        : item.QualStepName
                          ? `${item.QualStepName} | Funnel: ${item.Funnel || "N/A"} | Project: ${item.ProjectName || "N/A"}`
                          : item.ProjectName
                            ? `Project: ${item.ProjectName}`
                            : item.Id}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500">
              {searchQuery.length < minChars
                ? `Type at least ${minChars} characters to search`
                : "No results found"}
            </div>
          )}
        </div>
      )}

      {/* Show error if required and empty */}
      {showError && required && !value && !searchQuery && (
        <p className="text-xs text-red-600 mt-1">This field is required</p>
      )}
    </div>
  );
};

export default SearchableLookupField;
